import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, MessageSquare, Video } from 'lucide-react';
import axios from 'axios';

const VideoChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  
  // Predefined questions for the pill buttons
  const predefinedQuestions = [
    "How does this video relate to the topic?",
    "Can you explain the main concepts?"
  ];
  
  const API_URL = "https://bolofy.com/api/chatBot/get-message"; // Replace with actual API endpoint
  const SESSION_ID = "1f60959f-1b41-4eb0-96f6-e33456dfe53c";
  const CURRENT_URL = "https://bolofy.com/";
  const ACCESS_TOKEN = "eyJpdiI6ImQ2RWNMamorS3FnMzB6Ry9pOHgxMnc9PSIsInZhbHVlIjoiWExwMU0rcWpOR3RMMWVxZFQ5cU03K3JMWjRteTYzaVBJNHhqdnh4M2Z6Z2wxME96RmhtNGtxbGREWWdEc21uWTZlVzh5cm5MRVE5SjRXR0pET3JoTU1RU25yYjMyS0lZY09zUFhwak1BYU9OUEgyQ2NLVFVBQWdUaldMMWI1b0VOeEJGUm5UODNuemFhbnNIV0VFcDRRPT0iLCJtYWMiOiI1NjJhNDNlMmY1ZTg2NTg0NTE3MGYwOTNlMGE2OTE3MmRiMWZlMDRhNDlkYmQ3ZmU2ZTYzZDBkYTk2Yzg4ZGVlIiwidGFnIjoiIn0="; // Replace with actual token
  
  // Function to handle sending a message
  const sendMessage = async (text) => {
    // Add user message to chat
    const newMessages = [...messages, { text, sender: 'user', timestamp: new Date() }];
    setMessages(newMessages);
    setInputText('');
    setIsLoading(true);
    
    try {
      // Create abort controller for the axios request
      const controller = new AbortController();
      
      // Prepare the request payload
      const payload = {
        content: text.length > 500 ? text.substring(0, 500) : text, // Truncate if needed
        sessionId: SESSION_ID,
        url: CURRENT_URL
      };
      
      console.log(ACCESS_TOKEN, SESSION_ID, CURRENT_URL, payload.content);
      
      // Call the API using axios
      const response = await axios({
        method: 'POST',
        url: API_URL,
        headers: {
          'clientToken': `Bearer ${ACCESS_TOKEN}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        data: payload,
        signal: controller.signal
      });
      
      console.log('Response:', response.data);
      
      // Add bot response to chat
      setMessages([
        ...newMessages,
        {
          text: response.data.content,
          sender: 'bot',
          timestamp: new Date()
        }
      ]);
    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message to chat
      setMessages([
        ...newMessages,
        {
          text: "There was an error processing your request. Please try again.",
          sender: 'bot',
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsLoading(false);
      // Refocus the input field
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputText.trim() && !isLoading) {
      sendMessage(inputText);
    }
  };
  
  // Handle pill button click
  const handlePillClick = (question) => {
    if (!isLoading) {
      sendMessage(question);
    }
  };
  
  // Auto-scroll to the bottom of the chat when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-focus the input field on component mount and after sending a message
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  // Format time for message timestamps
  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    // <div className=" overflow-hidden min-h-screen w-screen bg-gradient-to-br from-blue-50 to-indigo-50 ">
    //   <motion.div 
    //     initial={{ opacity: 0, y: 20 }}
    //     animate={{ opacity: 1, y: 0 }}
    //     transition={{ duration: 0.5, ease: "easeOut" }}
    //     className="flex flex-col md:flex-row w-full h-screen   overflow-hidden shadow-xl"
    //   >
    
    <div className="h-screen w-screen bg-gradient-to-br from-blue-50 to-indigo-50 overflow-x-hidden overflow-y-auto">
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="flex flex-col md:flex-row h-full w-full overflow-y-auto shadow-xl"
    >
        {/* Video Container */}

<div className="w-full md:w-1/2 lg:w-[27rem] h-72 md:h-full "> 
  <video 
    className="w-full h-full object-cover"
    src="https://private-sync-user-generations-v2.s3.amazonaws.com/generations/cf073348-acce-447e-b6cc-121a1734e570/9ef8f773-f647-4410-a2d6-3a877384c6bd_stitcher/result.mp4" 
    autoPlay
    muted
    loop
    playsInline
    controls
  />
</div>
        
        {/* Chatbot Container */}
        <div className="w-full md:w-1/2 lg:w-[88rem] h-96 md:h-full flex flex-col bg-white border-t md:border-t-0 md:border-l border-gray-200">
          {/* Chat Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
            <div className="flex items-center">
              <MessageSquare size={18} className="mr-2" />
              <h3 className="font-medium">Live Chat Assistant</h3>
            </div>
            <span className="text-xs bg-green-500 px-2 py-1 rounded-full">Online</span>
          </div>
          
          {/* Chat Messages */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-blue-50 to-white"
          >
            <AnimatePresence>
              {messages.length === 0 && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center h-full text-center text-gray-500"
                >
                  <MessageSquare size={40} className="mb-2 text-blue-400 opacity-50" />
                  <p className="text-sm">Send a message or select a question below to start the conversation</p>
                </motion.div>
              )}
            </AnimatePresence>
            
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ 
                  duration: 0.4, 
                  ease: "easeOut",
                  delay: 0.1
                }}
                className={`mb-4 ${
                  message.sender === 'user' 
                    ? 'text-right' 
                    : 'text-left'
                }`}
              >
                <div className="flex flex-col">
                  {message.sender === 'user' ? (
                    <div className="flex flex-col items-end">
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl rounded-tr-none px-4 py-2 max-w-xs md:max-w-sm shadow-sm">
                        {message.text}
                      </div>
                      <span className="text-xs mt-1 text-gray-500">
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-start">
                      <div className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 rounded-2xl rounded-tl-none px-4 py-2 max-w-xs md:max-w-sm shadow-sm">
                        {message.text}
                      </div>
                      <span className="text-xs mt-1 text-gray-500">
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
            
            {/* Loading indicator */}
            <AnimatePresence>
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-start mt-2 mb-4"
                >
                  <div className="flex items-center space-x-1 bg-gray-100 rounded-full px-3 py-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Input and Buttons */}
          <div className="p-3 bg-white border-t border-gray-100">
            <form onSubmit={handleSubmit} className="mb-3">
              <div className={`flex items-center bg-gray-50 rounded-full pl-4 pr-1 py-1 border border-gray-200 ${!isLoading ? 'focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100' : 'opacity-75'} transition-all duration-200`}>
                <input
                  ref={inputRef}
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none text-sm"
                  placeholder="Type your message..."
                  disabled={isLoading}
                  autoFocus
                />
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  type="submit"
                  className={`ml-2 ${inputText.trim() && !isLoading ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-300 cursor-not-allowed'} text-white rounded-full p-2 transition-colors duration-200`}
                  disabled={!inputText.trim() || isLoading}
                >
                  <Send size={16} className="m-0.5" />
                </motion.button>
              </div>
            </form>
            
            {/* Pill Buttons */}
            <div className="flex flex-wrap gap-2">
              {predefinedQuestions.map((question, index) => (
                <motion.button
                  key={index}
                  whileHover={!isLoading ? { scale: 1.03 } : {}}
                  whileTap={!isLoading ? { scale: 0.97 } : {}}
                  onClick={() => handlePillClick(question)}
                  disabled={isLoading}
                  className={`bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200 hover:border-blue-300 rounded-full px-4 py-2 text-xs font-medium transition-all duration-200 whitespace-nowrap flex items-center ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <MessageSquare size={12} className="mr-1" />
                  {question}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Footer
      <div className="absolute bottom-1 text-xs text-center text-gray-500">
        <span>© 2025 Interactive Video Chat | Designed By Bolofy</span>
      </div> */}
    </div>
  );
};

export default VideoChatPage;